"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.usePhoneNumber = void 0;
var react_1 = require("react");
var google_libphonenumber_1 = require("google-libphonenumber");
var countryData_1 = require("../utils/countryData");
var phoneValidation_1 = require("../utils/phoneValidation");
var usePhoneNumber = function (_a) {
    var _b, _c;
    var _d = _a === void 0 ? {} : _a, initialCountry = _d.initialCountry, _e = _d.initialValue, initialValue = _e === void 0 ? '' : _e, _f = _d.enableCountryChange, enableCountryChange = _f === void 0 ? true : _f, preferredCountries = _d.preferredCountries;
    // Memoize countries data to prevent unnecessary recalculations
    var countries = (0, react_1.useMemo)(function () { return (0, countryData_1.getCountryData)(preferredCountries); }, [preferredCountries]);
    // Initialize selected country
    var _g = (0, react_1.useState)(function () {
        if (initialCountry) {
            var initialCountryData = countries.find(function (c) { return c.code === initialCountry; });
            if (initialCountryData)
                return initialCountryData;
        }
        return countries[0]; // Fallback to first country
    }), selectedCountry = _g[0], setSelectedCountryState = _g[1];
    var _h = (0, react_1.useState)(''), phoneNumber = _h[0], setPhoneNumberState = _h[1];
    var _j = (0, react_1.useState)(null), validationResult = _j[0], setValidationResult = _j[1];
    var _k = (0, react_1.useState)(null), autoDetectedCountryChange = _k[0], setAutoDetectedCountryChange = _k[1];
    var previousValueRef = (0, react_1.useRef)('');
    var isManualCountryChangeRef = (0, react_1.useRef)(false);
    // Process phone number with country-specific formatting and validation
    var processPhoneNumber = (0, react_1.useCallback)(function (phoneValue, countryCode, useNationalFormat) {
        if (useNationalFormat === void 0) { useNationalFormat = true; }
        var validation = (0, phoneValidation_1.validatePhoneNumber)(phoneValue, countryCode);
        if (useNationalFormat && validation.nationalFormatted) {
            return { formattedNumber: validation.nationalFormatted, validation: validation };
        }
        var formattedNumber = (0, phoneValidation_1.formatAsYouType)(phoneValue, countryCode, true);
        return { formattedNumber: formattedNumber, validation: validation };
    }, []);
    // Update country if different from current
    var updateCountryIfDifferent = (0, react_1.useCallback)(function (detectedCountryCode) {
        var detectedCountry = countries.find(function (c) { return c.code === detectedCountryCode; });
        if (detectedCountry && detectedCountry.code !== selectedCountry.code) {
            return detectedCountry;
        }
        return null;
    }, [countries, selectedCountry.code]);
    // Handle external value changes (controlled component behavior)
    (0, react_1.useEffect)(function () {
        if (previousValueRef.current === initialValue)
            return;
        previousValueRef.current = initialValue || '';
        if (!initialValue) {
            setPhoneNumberState('');
            setValidationResult(null);
            return;
        }
        // Try to extract country code from the input value
        var detectedCountryCode = (0, phoneValidation_1.extractCountryCodeFromNumber)(initialValue, selectedCountry.code);
        if (detectedCountryCode &&
            enableCountryChange &&
            !isManualCountryChangeRef.current) {
            // Update country if different and country change is enabled
            // but only if this is not during a manual country change
            var newCountry = updateCountryIfDifferent(detectedCountryCode);
            if (newCountry) {
                var previousCountry = selectedCountry;
                setSelectedCountryState(newCountry);
                // Trigger auto-detected country change notification
                setAutoDetectedCountryChange({
                    newCountry: newCountry,
                    previousCountry: previousCountry,
                });
            }
            // Process with detected country
            var _a = processPhoneNumber(initialValue, detectedCountryCode, true), formattedNumber = _a.formattedNumber, validation = _a.validation;
            setPhoneNumberState(formattedNumber);
            setValidationResult(validation);
        }
        else {
            // Use current selected country
            var _b = processPhoneNumber(initialValue, selectedCountry.code, false), formattedNumber = _b.formattedNumber, validation = _b.validation;
            setPhoneNumberState(formattedNumber);
            setValidationResult(validation);
        }
    }, [
        initialValue,
        selectedCountry.code,
        enableCountryChange,
        processPhoneNumber,
        updateCountryIfDifferent,
    ]);
    // Handle phone number changes
    var setPhoneNumber = (0, react_1.useCallback)(function (newPhoneNumber) {
        // Reset manual country change flag when user types
        isManualCountryChangeRef.current = false;
        // Format as you type using the selected country
        var formattedNumber = (0, phoneValidation_1.formatAsYouType)(newPhoneNumber, selectedCountry.code, enableCountryChange);
        setPhoneNumberState(formattedNumber);
        // Validate based on enableCountryChange setting
        var validation;
        if (enableCountryChange) {
            // Auto-detection enabled: validate with auto-detection first, then fallback to selected country
            validation = (0, phoneValidation_1.validatePhoneNumber)(formattedNumber);
            if (!validation.countryCode || !validation.isValid) {
                validation = (0, phoneValidation_1.validatePhoneNumber)(formattedNumber, selectedCountry.code);
            }
            // Auto-update country if detected and different from current
            // but only if this is not a manual country change
            if (!isManualCountryChangeRef.current &&
                validation.countryCode &&
                validation.countryCode !== selectedCountry.code) {
                var detectedCountry = countries.find(function (c) { return c.code === validation.countryCode; });
                if (detectedCountry) {
                    var previousCountry = selectedCountry;
                    setSelectedCountryState(detectedCountry);
                    // Trigger auto-detected country change notification only if this is truly an auto-detection
                    // (not during manual country changes or reformatting)
                    setAutoDetectedCountryChange({
                        newCountry: detectedCountry,
                        previousCountry: previousCountry,
                    });
                }
            }
        }
        else {
            // Auto-detection disabled: only validate against the selected country
            // If input starts with +, treat it as invalid for the selected country
            // unless it matches the selected country's international prefix
            var phoneUtil = google_libphonenumber_1.PhoneNumberUtil.getInstance();
            var countryCallingCode = phoneUtil.getCountryCodeForRegion(selectedCountry.code);
            var expectedPrefix = "+".concat(countryCallingCode);
            if (formattedNumber.startsWith('+') &&
                !formattedNumber.startsWith(expectedPrefix)) {
                // International number that doesn't match the selected country
                validation = {
                    isValid: false,
                    formattedNumber: formattedNumber,
                    countryCode: selectedCountry.code,
                    nationalNumber: formattedNumber,
                    nationalFormatted: formattedNumber,
                    errorMessage: 'Please enter a valid phone number for the selected country',
                };
            }
            else {
                // National number or correct international prefix - validate against selected country
                validation = (0, phoneValidation_1.validatePhoneNumber)(formattedNumber, selectedCountry.code);
            }
        }
        setValidationResult(validation);
    }, [selectedCountry, countries, enableCountryChange]);
    // Handle country changes
    var setSelectedCountry = (0, react_1.useCallback)(function (country) {
        var previousCountry = selectedCountry;
        // Set flag to prevent auto-detection during manual country change
        isManualCountryChangeRef.current = true;
        setSelectedCountryState(country);
        // Reformat existing phone number with new country's rules
        if (phoneNumber) {
            var reformattedNumber = void 0;
            // Try to swap dial codes properly if the number has a dial code
            if (phoneNumber.includes('+') || phoneNumber.length > 7) {
                // Attempt to swap dial codes for numbers that might be international
                reformattedNumber = (0, phoneValidation_1.swapDialCode)(phoneNumber, previousCountry.code, country.code);
            }
            else {
                // For short numbers or clearly national numbers, just reformat with new country
                reformattedNumber = (0, phoneValidation_1.formatAsYouType)(phoneNumber, country.code, true);
            }
            setPhoneNumberState(reformattedNumber);
            // Re-validate with new country
            var validation = (0, phoneValidation_1.validatePhoneNumber)(reformattedNumber, country.code);
            setValidationResult(validation);
        }
        else {
            setValidationResult(null);
        }
        // Clear the manual country change flag after a longer delay to ensure all state updates are complete
        // This prevents auto-detection from interfering with modal closing
        setTimeout(function () {
            isManualCountryChangeRef.current = false;
        }, 300);
    }, [selectedCountry, phoneNumber]);
    // Clear auto-detected country change
    var clearAutoDetectedCountryChange = (0, react_1.useCallback)(function () {
        setAutoDetectedCountryChange(null);
    }, []);
    return {
        // State
        phoneNumber: phoneNumber,
        selectedCountry: selectedCountry,
        validationResult: validationResult,
        countries: countries,
        // Actions
        setPhoneNumber: setPhoneNumber,
        setSelectedCountry: setSelectedCountry,
        // Auto-detection
        autoDetectedCountryChange: autoDetectedCountryChange,
        clearAutoDetectedCountryChange: clearAutoDetectedCountryChange,
        // Computed
        isValid: (_b = validationResult === null || validationResult === void 0 ? void 0 : validationResult.isValid) !== null && _b !== void 0 ? _b : false,
        errorMessage: validationResult === null || validationResult === void 0 ? void 0 : validationResult.errorMessage,
        formattedE164: (_c = validationResult === null || validationResult === void 0 ? void 0 : validationResult.formattedNumber) !== null && _c !== void 0 ? _c : '',
        phoneNumberType: (validationResult === null || validationResult === void 0 ? void 0 : validationResult.phoneNumberType) !== undefined
            ? String(validationResult.phoneNumberType)
            : undefined,
    };
};
exports.usePhoneNumber = usePhoneNumber;
//# sourceMappingURL=usePhoneNumber.js.map